import { setup, assign } from "xstate"
import type { RouteLocationNormalizedLoaded } from "vue-router"

// ================================
// TYPES
// ================================

export interface FormAuthFirstLoginContext {
    user: { _id?: string | null; username?: string | null }
    validation: string | null
    idcompany: string | null
    hascompany: boolean | null
}

export interface FormAuthFirstLoginInput {
    getRoute: () => RouteLocationNormalizedLoaded
}

export type FormAuthFirstLoginEvent =
    | { type: "REDIRECTION" }
    | { type: "NEXT"; value?: FormAuthFirstLoginContext["user"] }
    | {
          type: "NEXT"
          validation?: string
          idcompany?: string | null
          hascompany?: boolean
      }

// ================================
// MACHINE
// ================================

/**
 * Machine à états pour le parcours première connexion.
 * États : idle → FormAuthFirstLoginEmail | FormAuthFirstLoginConfirmation → FormAuthFirstLoginConfirmation → FormAuthFirstLoginInfo.
 */
export function createFormAuthFirstLoginMachine(
    input: FormAuthFirstLoginInput
) {
    return setup({
        types: {
            input: {} as FormAuthFirstLoginInput,
            context: {} as FormAuthFirstLoginContext,
            events: {} as FormAuthFirstLoginEvent,
        },
        actions: {
            updateUser: assign({
                user: ({ event }) =>
                    event.type === "NEXT" && "value" in event && event.value
                        ? (event.value as FormAuthFirstLoginContext["user"])
                        : { _id: null, username: null },
            }),
            updateValidation: assign({
                validation: ({ event }) =>
                    event.type === "NEXT" && "validation" in event
                        ? (event.validation ?? null)
                        : null,
            }),
            updateIdcompany: assign({
                idcompany: ({ event }) =>
                    event.type === "NEXT" && "idcompany" in event
                        ? (event.idcompany ?? null)
                        : null,
            }),
            updateHascompany: assign({
                hascompany: ({ event }) =>
                    event.type === "NEXT" && "hascompany" in event
                        ? (event.hascompany ?? null)
                        : null,
            }),
        },
    }).createMachine({
        id: "FormAuthFirstLogin",
        initial: "idle",
        context: ({ input: inp }) => {
            const route = inp.getRoute()
            return {
                user: {
                    _id: (route.query._id as string) || null,
                    username: (route.query.username as string) || null,
                },
                validation: null,
                idcompany: (route.query.idcompany as string) || null,
                hascompany:
                    (route.query.hascompany as string) === "true" ||
                    (route.query.hascompany as string) === "1"
                        ? true
                        : null,
            }
        },
        states: {
            idle: {
                on: {
                    REDIRECTION: [
                        {
                            target: "FormAuthFirstLoginConfirmation",
                            guard: () => {
                                const route = input.getRoute()
                                return (
                                    route?.query?.validationCode !== undefined
                                )
                            },
                        },
                        {
                            target: "FormAuthFirstLoginEmail",
                        },
                    ],
                },
            },
            FormAuthFirstLoginEmail: {
                on: {
                    NEXT: {
                        target: "FormAuthFirstLoginConfirmation",
                        actions: ["updateUser"],
                    },
                },
            },
            FormAuthFirstLoginConfirmation: {
                on: {
                    NEXT: {
                        target: "FormAuthFirstLoginInfo",
                        actions: [
                            "updateValidation",
                            "updateIdcompany",
                            "updateHascompany",
                        ],
                    },
                },
            },
            FormAuthFirstLoginInfo: {
                on: {},
            },
        },
    })
}
